import React from 'react'
import { useQueryParams } from '@fs/zion-router'
import { useFeatureFlag } from '@fs/zion-flags'
import { usePageWidth } from '@fs/zion-ui'
import { useOverlayContext } from '../../../shared/providers/overlayContextProvider/OverlayContext'
import IndexImage from '../../views/indexing/components/indexImage/IndexImage'
import SelectPerson from '../../views/indexing/components/selectPerson/SelectPerson'
import SelectPersonBottomSheet from '../../views/indexing/components/selectPerson/SelectPersonBottomSheet'
import FullText from '../../views/fullText/FullText'
import { useR2StateContext } from '../../providers/r2Provider/R2Context'
import SelectImagePrompt from './SelectImagePrompt'
import useInfoSheetOrientationCachedState from '../../../shared/hooks/useInfoSheetOrientationCachedState'
import TranscriptAndNamesPanel from '../../views/indexing/components/transcriptAndNamesPanel/TranscriptAndNamesPanel'

/**
 * SideSheetBody
 *
 * Renders the main body content of the side sheet, conditionally displaying different views
 * based on feature flags and query parameters (grid view, index view, full text view).
 *
 * Handles two feature flag variations:
 * - V2 (rip_sharedImageViewer_fullTextAttachFlow_v2): Shows TranscriptAndNamesPanel with IndexImage
 * - V1 (legacy): Shows SelectPerson/SelectPersonBottomSheet with conditional IndexImage and FullText
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.imageHasData - Whether the current image has indexing data
 * @param {boolean} [props.loading=false] - Loading state indicator
 * @param {boolean} props.fullTextAttachFlowV1IsOn - Feature flag for V1 full text attach flow
 * @returns {JSX.Element}
 */
const SideSheetBody = ({ imageHasData, loading = false, fullTextAttachFlowV1IsOn }) => {
  const { query } = useQueryParams()
  const { view, grid } = query || {}
  const r2State = useR2StateContext()
  const { inPanelStack } = useOverlayContext()
  const pageWidth = usePageWidth()
  const isMobile = pageWidth({ default: true, sm: false })
  const fullTextAttachFlowV2Flag = useFeatureFlag('rip_sharedImageViewer_fullTextAttachFlow_v2')
  const { isBottomSheet } = useInfoSheetOrientationCachedState()

  return (
    <>
      {fullTextAttachFlowV2Flag.isOn ? (
        <>
          {grid && view === 'index' ? (
            <SelectImagePrompt />
          ) : (
            <>
              <TranscriptAndNamesPanel loading={loading} />
              {(imageHasData || inPanelStack('addPersonPage1Generic')) && !r2State.resetState && <IndexImage />}
            </>
          )}
        </>
      ) : (
        <>
          {grid && view === 'index' && <SelectImagePrompt />}
          {!grid && view === 'index' && (
            <>
              {!isMobile && (
                <>
                  {isBottomSheet && <SelectPersonBottomSheet />}
                  {!isBottomSheet && <SelectPerson loading={loading} />}
                </>
              )}
              {isMobile && <SelectPerson loading={loading} />}
              {imageHasData && !r2State.resetState && <IndexImage />}
            </>
          )}
          {view === 'fullText' && <FullText fullTextAttachFlowV1IsOn={fullTextAttachFlowV1IsOn} />}
          {/* This only shows when V2 is off */}
        </>
      )}
    </>
  )
}

export default SideSheetBody
